import React, { useEffect } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from '../../../../hooks/hooks';
import { Client } from '../../../../features/clients/types';
import { updateClient, getClients } from '../../../../features/clients';
import { filterClients } from '../../../../features/clients/filter';
import BaseForm from '../../../../components/shared/form-controls/base-form';
import { NAVIGATION_ROUTES } from '../../../../routes/NavigationRoute';
import { FormInput } from '../../../../components/shared/form-controls/form-input';
import { FormCheckbox } from '../../../../components/shared/form-controls/form-checkbox';
import { Lx } from '../../../../i18n/consts';
import { canManageClients } from '../../../../features/profile/entityPermissions';

interface EditClientState {
    client: Client;
}

interface EditFormValues {
    name: string;
    isActive: boolean;
    contactPerson?: string;
    email?: string;
    site?: string;
    note?: string;
}

export default function EditClientForm() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const intl = useIntl();
    const location = useLocation();
    const state = location.state as EditClientState | undefined;
    const profile = useAppSelector(store => store.profile);
    const canEditClients = canManageClients(profile);

    useEffect(() => {
        if (!state?.client || !canEditClients) {
            navigate(NAVIGATION_ROUTES.CLIENTS);
        }
    }, [canEditClients, navigate, state]);

    if (!state?.client || !canEditClients) {
        return null;
    }

    const initialValues: EditFormValues = {
        name: state.client.name ?? '',
        isActive: state.client.isActive ?? true,
        contactPerson: state.client.contactPerson ?? '',
        email: state.client.email ?? '',
        site: state.client.site ?? '',
        note: state.client.note ?? '',
    };

    const validateName = async (name: string): Promise<boolean> => {
        const trimmed = name.trim().toLowerCase();
        if (trimmed === '') {
            return false;
        }

        if (trimmed === (state.client.name ?? '').toLowerCase()) {
            return true;
        }

        const matches = await dispatch(filterClients(trimmed)).unwrap();
        return matches.length < 1 || matches.every(item => item.name.toLowerCase() !== trimmed);
    };

    const validationSchema: Yup.Schema<EditFormValues> = Yup.object({
        name: Yup.string()
            .max(45, intl.formatMessage({ id: Lx.Form.MAX_LENGTH_ERROR }))
            .required(intl.formatMessage({ id: Lx.Form.NAME_REQUIRED_FORMAT }, { s: intl.formatMessage({ id: Lx.General.CLIENT }) }))
            .test(
                'client-name-backend-validation',
                intl.formatMessage({ id: Lx.Client.NAME_TAKEN }),
                validateName
            ),
        isActive: Yup.boolean().required(),
        contactPerson: Yup.string().optional(),
        email: Yup.string().email().optional(),
        site: Yup.string().optional(),
        note: Yup.string().optional(),
    });

    const handleSubmit = async (values: EditFormValues) => {
        const payload: Client = {
            id: state.client.id,
            name: values.name,
            isActive: values.isActive,
            contactPerson: values.contactPerson ?? '',
            email: values.email ?? '',
            site: values.site ?? '',
            note: values.note ?? '',
        };

        await dispatch(updateClient(payload)).unwrap();
        await dispatch(getClients(0)).unwrap();
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            validateOnBlur={true}
            validateOnChange={true}
            onSubmit={handleSubmit}>
            <Form>
                <BaseForm
                    contentGap={24}
                    formType='edit'
                    formHeader={Lx.Client.EDIT_CLIENT}
                    elementName={state.client.name}
                    savingTitle={intl.formatMessage({ id: Lx.General.CLIENT })}
                    onClose={() => navigate(NAVIGATION_ROUTES.CLIENTS)}
                    onAfterSave={() => navigate(NAVIGATION_ROUTES.CLIENTS)}>
                    <FormInput
                        name='name'
                        label={intl.formatMessage({ id: Lx.General.NAME })}
                        placeholder={intl.formatMessage({ id: Lx.General.NAME })}
                        limited={true}
                        required={true}
                        style={{ width: '100%', maxWidth: '500px' }}
                    />
                    <FormInput
                        name='contactPerson'
                        label={intl.formatMessage({ id: Lx.Client.CONTACT_PERSON })}
                        placeholder={intl.formatMessage({ id: Lx.Client.CONTACT_PERSON })}
                        style={{ width: '100%', maxWidth: '500px' }}
                    />
                    <FormInput
                        name='email'
                        label={intl.formatMessage({ id: Lx.General.EMAIL })}
                        placeholder={intl.formatMessage({ id: Lx.General.EMAIL })}
                        style={{ width: '100%', maxWidth: '500px' }}
                    />
                    <FormInput
                        name='site'
                        label={intl.formatMessage({ id: Lx.Client.SITE })}
                        placeholder={intl.formatMessage({ id: Lx.Client.SITE })}
                        style={{ width: '100%', maxWidth: '500px' }}
                    />
                    <FormInput
                        name='note'
                        label={intl.formatMessage({ id: Lx.Client.NOTE })}
                        placeholder={intl.formatMessage({ id: Lx.Client.NOTE })}
                        style={{ width: '100%', maxWidth: '500px' }}
                    />
                    <FormCheckbox
                        name='isActive'
                        label={intl.formatMessage({ id: Lx.General.ACTIVE })}
                        style={{ marginBottom: 0 }}
                    />
                </BaseForm>
            </Form>
        </Formik>
    );
}
